import { useCallback, useMemo, useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Dialog, DialogContent, RadioGroup } from "@tenant/ui";
import type { SeatType } from "./hooks/useConnectedPassengerSelection";
import styles from "./InfantSeatPickerDialog.module.scss";

export interface InfantSeatPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onContinue: (seatType: SeatType) => void;
}

export const InfantSeatPickerDialog = (props: InfantSeatPickerDialogProps) => {
  const { open, onClose, onContinue } = props;
  const [block, cn] = useModuleBEM(styles, "InfantSeatPickerDialog");
  const { t } = useI18nContext();
  const [selectedOption, setSelectedOption] = useState<SeatType>("OnLap");

  const options = useMemo(
    () => [
      {
        label: t("checkoutSection.travelerForm.infant.onLap"),
        value: "OnLap",
      },
      {
        label: t("checkoutSection.travelerForm.infant.ownSeat"),
        value: "OwnSeat",
      },
      {
        label: t("checkoutSection.travelerForm.infant.notTraveling"),
        value: "NotTraveling",
      },
    ],
    [t]
  );

  const doContinue = useCallback(() => {
    if (selectedOption == null) {
      onClose();
    } else {
      onContinue(selectedOption);
    }
  }, [onClose, onContinue, selectedOption]);

  return (
    <Dialog open={open}>
      <DialogContent
        title={t("infantModal.title")}
        showCloseButton={false}
        className={block}
        bottomFixedContent={
          <section className={cn("bottom-content")}>
            <button className={cn("save-button")} onClick={doContinue}>
              {t("savePassenger")}
            </button>
            <button className={cn("cancel-button")} onClick={onClose}>
              {t("cancel")}
            </button>
          </section>
        }
      >
        <RadioGroup
          options={options}
          classNames={{
            container: cn("options"),
            itemContainer: cn("option"),
          }}
          onSelectValue={(value: SeatType) => setSelectedOption(value)}
          selectedOption={selectedOption ?? ""}
        />
      </DialogContent>
    </Dialog>
  );
};
